import React, { useState, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Link2, Loader2, Users, Shirt, TreePine, Package, Upload, X, Sparkles, Send, Pencil, Maximize2, ChevronDown, Wand2, FileText, RefreshCw, Image as ImageIcon, Copy, Check, PenLine } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TONALITIES = ['Humour', 'Emotional', 'Aspirational', 'Informative', 'Dramatic', 'Edgy/Bold', 'Minimalist'];

const CCC_SLOTS = [
  { key: 'cast', label: 'Cast', icon: Users, color: '#EC4899' },
  { key: 'costume', label: 'Costume', icon: Shirt, color: '#F59E0B' },
  { key: 'environment', label: 'Corner', icon: TreePine, color: '#10B981' },
];

// Poll task (same pattern as montage)
async function pollTask(taskId, token) {
  const maxPolls = 100;
  let notFoundCount = 0;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, 3000));
    let data;
    try {
      const resp = await axios.get(`${API}/api/recipes/url-to-ad/task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      data = resp.data;
      notFoundCount = 0;
    } catch (err) {
      if (err.response?.status === 404) {
        notFoundCount++;
        if (notFoundCount >= 3) throw new Error('Task not found — it may have expired. Please try again.');
        continue;
      }
      continue;
    }
    if (data.status === 'done') return data;
    if (data.status === 'failed') throw new Error(data.error || 'Task failed');
  }
  throw new Error('Task timed out');
}

// Poll montage task (for CCC iteration)
async function pollMontageTask(taskId, token) {
  const maxPolls = 100;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, 3000));
    let data;
    try {
      const resp = await axios.get(`${API}/api/recipes/montage-task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      data = resp.data;
    } catch (err) {
      if (err.response?.status === 404) throw new Error('Task not found');
      continue;
    }
    if (data.status === 'done') return data;
    if (data.status === 'failed') throw new Error(data.error || 'Task failed');
  }
  throw new Error('Task timed out');
}

export default function URLtoAdInputs({ values, onChange, token, onGenerate, onVideoStateChange, restoredData, onReset }) {
  const [url, setUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [product, setProduct] = useState(null);
  const [tonality, setTonality] = useState('Informative');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [idea, setIdea] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [ctaText, setCtaText] = useState('');

  // Script state
  const [script, setScript] = useState(null);
  const [generatingScript, setGeneratingScript] = useState(false);

  // Shots state
  const [shots, setShots] = useState(null);
  const [generatingShots, setGeneratingShots] = useState(false);

  // Video generation state
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  // Restore data from previous run
  const [restoredApplied, setRestoredApplied] = useState(null);
  React.useEffect(() => {
    if (restoredData && restoredData !== restoredApplied) {
      if (restoredData.product) setProduct(restoredData.product);
      if (restoredData.script) setScript(restoredData.script);
      if (restoredData.shots) setShots(restoredData.shots);
      if (restoredData.tonality) setTonality(restoredData.tonality);
      if (restoredData.aspectRatio) setAspectRatio(restoredData.aspectRatio);
      if (restoredData.idea) setIdea(restoredData.idea);
      if (restoredData.targetAudience) setTargetAudience(restoredData.targetAudience);
      if (restoredData.ctaText) setCtaText(restoredData.ctaText);
      if (restoredData.sourceUrl) setUrl(restoredData.sourceUrl);
      setRestoredApplied(restoredData);
    }
  }, [restoredData]);

  // Script/Shots editing state
  const [editingScript, setEditingScript] = useState(false);
  const [editingShots, setEditingShots] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);
  const [copiedShots, setCopiedShots] = useState(false);

  // CCC iteration state
  const [editing, setEditing] = useState(null);
  const [editFeedback, setEditFeedback] = useState('');
  const [editRef, setEditRef] = useState(null);
  const [generating, setGenerating] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const editRefInput = useRef(null);

  const updateSlot = (key, patch) => {
    onChange({ ...values, [key]: { ...values[key], ...patch } });
  };

  const handleFileSelect = (key, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    updateSlot(key, { file, preview, processedUrl: null, text: '' });
    e.target.value = '';
  };

  const clearSlot = (key) => {
    updateSlot(key, { file: null, preview: null, processedUrl: null, text: '' });
    const input = document.getElementById(`ad-file-${key}`);
    if (input) input.value = '';
  };

  // CCC text-to-image generation (reuse montage endpoint)
  const generateFromText = async (key) => {
    const text = values[key]?.text?.trim();
    if (!text) return;
    setGenerating(g => ({ ...g, [key]: true }));
    try {
      const { data } = await axios.post(`${API}/api/recipes/generate-slot-image`, { text, slot_type: key }, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
      if (!data.task_id) throw new Error('No task_id');
      const result = await pollMontageTask(data.task_id, token);
      if (result.url) {
        try {
          const resp = await fetch(result.url);
          const blob = await resp.blob();
          const dataUrl = await new Promise(r => { const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(blob); });
          updateSlot(key, { preview: dataUrl, processedUrl: result.url, file: null });
        } catch { updateSlot(key, { preview: result.url, processedUrl: result.url, file: null }); }
        setExpanded(null);
      }
    } catch (err) { alert(err.message || 'Generation failed'); }
    finally { setGenerating(g => ({ ...g, [key]: false })); }
  };

  // CCC iteration (reuse montage endpoint)
  const iterateImage = async (key) => {
    if (!editFeedback.trim()) return;
    const slot = values[key] || {};
    const imageUrl = slot.processedUrl || '';
    setGenerating(g => ({ ...g, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('feedback', editFeedback.trim());
      formData.append('slot_type', key);
      if (imageUrl) formData.append('image_url', imageUrl);
      if (!imageUrl && slot.file) formData.append('image', slot.file);
      if (editRef?.file) formData.append('ref_image', editRef.file);
      const { data } = await axios.post(`${API}/api/recipes/iterate-slot-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 15000,
      });
      if (!data.task_id) throw new Error('No task_id');
      const result = await pollMontageTask(data.task_id, token);
      if (result.url) {
        try {
          const resp = await fetch(result.url);
          const blob = await resp.blob();
          const dataUrl = await new Promise(r => { const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(blob); });
          updateSlot(key, { preview: dataUrl, processedUrl: result.url, file: null });
        } catch { updateSlot(key, { preview: result.url, processedUrl: result.url, file: null }); }
        setEditFeedback(''); setEditRef(null); setEditing(null);
      }
    } catch (err) { alert(err.message || 'Iteration failed'); }
    finally { setGenerating(g => ({ ...g, [key]: false })); }
  };

  // Extract product from URL
  const extractProduct = async () => {
    if (!url.trim()) return;
    setExtracting(true);
    setProduct(null);
    try {
      const { data } = await axios.post(`${API}/api/recipes/url-to-ad/extract`, { url: url.trim() }, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
      if (!data.task_id) throw new Error('No task_id');
      const result = await pollTask(data.task_id, token);
      if (result.product) {
        setProduct(result.product);
        // Auto-set SKU slot with first product image
        if (result.product.product_images?.length > 0) {
          updateSlot('sku', { preview: result.product.product_images[0], processedUrl: result.product.product_images[0], file: null });
        }
      }
    } catch (err) { alert(err.message || 'Extraction failed'); }
    finally { setExtracting(false); }
  };

  // Generate Script (Prompt 1)
  const handleGenerateScript = async () => {
    if (!product) return;
    setGeneratingScript(true);
    setScript(null);
    setShots(null);
    try {
      const { data } = await axios.post(`${API}/api/recipes/url-to-ad/generate-script`, {
        product_name: product.product_name,
        brand: product.brand,
        category: product.category,
        description: product.description,
        key_features: product.key_features,
        price: product.price,
        tonality,
        idea,
        target_audience: targetAudience,
        cta_text: ctaText,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
      if (!data.task_id) throw new Error('No task_id');
      const result = await pollTask(data.task_id, token);
      if (result.script) setScript(result.script);
    } catch (err) { alert(err.message || 'Script generation failed'); }
    finally { setGeneratingScript(false); }
  };

  // Generate Shot Breakdown (Prompt 2)
  const handleGenerateShots = async () => {
    if (!script || !product) return;
    setGeneratingShots(true);
    setShots(null);
    try {
      const { data } = await axios.post(`${API}/api/recipes/url-to-ad/generate-shots`, {
        product_name: product.product_name,
        brand: product.brand,
        category: product.category,
        description: product.description,
        key_features: product.key_features,
        tonality,
        script: script.script,
        ending_line: script.ending_line,
        cta_text: script.cta || ctaText,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
      if (!data.task_id) throw new Error('No task_id');
      const result = await pollTask(data.task_id, token);
      if (result.shots) setShots(result.shots);
    } catch (err) { alert(err.message || 'Shot breakdown failed'); }
    finally { setGeneratingShots(false); }
  };

  const getImgSrc = (slot) => {
    if (!slot) return null;
    if (slot.preview) return slot.preview;
    if (slot.processedUrl?.startsWith('http')) return slot.processedUrl;
    return null;
  };

  return (
    <div data-testid="url-to-ad-inputs" className="space-y-3">

      {/* ── URL Input ── */}
      <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <label className="text-[10px] text-white/50 block mb-1.5">Product URL</label>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="Paste Amazon product URL..."
              className="w-full pl-8 pr-3 py-2.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] text-white placeholder-white/20 focus:outline-none focus:border-cyan-500/30"
              onKeyDown={e => { if (e.key === 'Enter') extractProduct(); }}
              data-testid="url-input"
            />
          </div>
          <button
            onClick={extractProduct}
            disabled={extracting || !url.trim()}
            className="px-4 py-2.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40 flex items-center gap-1.5"
            style={{ background: 'linear-gradient(135deg, #0EA5E9, #0284C7)', color: '#fff' }}
            data-testid="extract-btn"
          >
            {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Extract
          </button>
        </div>
      </div>

      {/* ── Product Card (after extraction) ── */}
      {product && (
        <div className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(14,165,233,0.04)', border: '1px solid rgba(14,165,233,0.12)' }} data-testid="product-card">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <input
                value={product.product_name}
                onChange={e => setProduct({ ...product, product_name: e.target.value })}
                className="w-full text-sm font-semibold bg-transparent text-white border-b border-transparent hover:border-white/10 focus:border-cyan-500/30 focus:outline-none pb-0.5 truncate"
                data-testid="product-name-input"
              />
              <span className="text-[9px] text-cyan-400/60 mt-0.5 block">{product.brand} {product.category ? `/ ${product.category}` : ''} {product.price ? `/ ${product.price}` : ''}</span>
            </div>
          </div>
          <textarea
            value={product.description}
            onChange={e => setProduct({ ...product, description: e.target.value })}
            rows={2}
            className="w-full text-[11px] bg-white/[0.03] border border-white/[0.06] rounded-lg px-2.5 py-2 text-white/70 focus:outline-none focus:border-cyan-500/20 resize-none"
            data-testid="product-desc-input"
          />
          {product.key_features?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {product.key_features.map((f, i) => (
                <span key={i} className="text-[9px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300/70 border border-cyan-500/10">{f}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── SKU Display — always show when product exists ── */}
      {product && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(139,92,246,0.15)' }} data-testid="sku-display">
          <div className="flex items-center justify-between px-3 py-2" style={{ background: 'rgba(139,92,246,0.06)', borderBottom: '1px solid rgba(139,92,246,0.1)' }}>
            <div className="flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-violet-400" />
              <span className="text-[10px] font-medium text-white/70">SKU — Product Images</span>
            </div>
            <label htmlFor="sku-image-upload" className="text-[9px] text-violet-400/60 hover:text-violet-400 cursor-pointer flex items-center gap-1">
              <Upload className="w-2.5 h-2.5" /> Add
            </label>
            <input id="sku-image-upload" type="file" accept="image/*" multiple className="hidden" onChange={e => {
              const files = Array.from(e.target.files || []);
              const urls = files.map(f => URL.createObjectURL(f));
              setProduct({ ...product, product_images: [...(product.product_images || []), ...urls] });
              e.target.value = '';
            }} />
          </div>
          <div className="p-2">
            {product.product_images?.length > 0 ? (
              <div className="grid grid-cols-3 gap-1.5">
                {product.product_images.slice(0, 6).map((img, i) => (
                  <div key={i} className="relative rounded-lg overflow-hidden group">
                    <img src={img} alt={`Product ${i + 1}`} className="w-full aspect-square object-contain bg-white rounded-lg cursor-pointer" onClick={() => setLightbox({ src: img, label: `Product Image ${i + 1}` })} />
                    <button
                      onClick={() => setProduct({ ...product, product_images: product.product_images.filter((_, j) => j !== i) })}
                      className="absolute top-1 right-1 p-0.5 bg-black/60 hover:bg-black/80 rounded-full z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-product-img-${i}`}
                    >
                      <X className="w-2.5 h-2.5 text-white/80" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <label htmlFor="sku-image-upload" className="flex flex-col items-center justify-center py-4 cursor-pointer opacity-50 hover:opacity-70 transition-opacity">
                <Upload className="w-5 h-5 text-violet-400/40 mb-1" />
                <span className="text-[9px] text-white/30">Add product images</span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* ── CCC Row (Cast, Costume, Corner) ── */}
      <div className="grid grid-cols-3 gap-2">
        {CCC_SLOTS.map(({ key, label, icon: Icon, color }) => {
          const slot = values[key] || {};
          const isGenerating = generating[key];
          const hasImage = slot.file || slot.preview || slot.processedUrl;

          return (
            <div key={key} className="rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${hasImage ? color + '30' : 'rgba(255,255,255,0.06)'}` }}>
              <div className="flex items-center gap-1 px-2 py-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <Icon className="w-3 h-3" style={{ color }} />
                <span className="text-[9px] font-medium text-white/60">{label}</span>
              </div>
              <div className="p-1">
                <input type="file" id={`ad-file-${key}`} accept="image/*" onChange={e => handleFileSelect(key, e)} className="hidden" />
                {hasImage ? (
                  <div className="relative rounded overflow-hidden" data-testid={`ad-${key}-preview`}>
                    <img src={getImgSrc(slot)} alt={label} className="w-full aspect-square object-cover rounded pointer-events-none select-none" draggable={false} />
                    <button className="absolute inset-0 z-[1] bg-transparent cursor-pointer" onClick={() => setLightbox({ src: getImgSrc(slot), label })} aria-label={`Enlarge ${label}`} />
                    <div className="absolute bottom-0.5 left-0.5 p-0.5 bg-black/50 rounded-full pointer-events-none z-[2]">
                      <Maximize2 className="w-2 h-2 text-white/60" />
                    </div>
                    <div className="absolute top-0.5 right-0.5 flex gap-0.5 z-[3]">
                      <button onClick={() => { setEditing(editing === key ? null : key); setExpanded(null); setEditFeedback(''); setEditRef(null); }} className="p-0.5 bg-black/60 rounded-full" data-testid={`ad-${key}-edit`}>
                        <Pencil className="w-2 h-2 text-white/70" />
                      </button>
                      <button onClick={() => clearSlot(key)} className="p-0.5 bg-black/60 rounded-full" data-testid={`ad-${key}-clear`}>
                        <X className="w-2 h-2 text-white/70" />
                      </button>
                    </div>
                  </div>
                ) : isGenerating ? (
                  <div className="aspect-square rounded bg-white/[0.03] flex items-center justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color }} />
                  </div>
                ) : (
                  <div className="relative aspect-[4/3] rounded border border-dashed border-white/[0.08] flex flex-col items-center justify-center gap-1.5">
                    <label
                      htmlFor={`ad-file-${key}`}
                      className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
                      data-testid={`ad-${key}-upload`}
                    >
                      <Upload className="w-4 h-4 text-white/20" />
                      <span className="text-[8px] text-white/25">Upload</span>
                    </label>
                    <button
                      onClick={() => { setExpanded(expanded === key ? null : key); setEditing(null); }}
                      className="relative z-10 text-[8px] text-white/25 hover:text-white/40 px-2 py-0.5 rounded mt-auto mb-1"
                      data-testid={`ad-${key}-describe`}
                    >
                      <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />AI
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Describe panel */}
      {expanded && (
        <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-white/50">Describe {CCC_SLOTS.find(s => s.key === expanded)?.label}</span>
            <button onClick={() => setExpanded(null)} className="p-0.5"><X className="w-3 h-3 text-white/30" /></button>
          </div>
          <div className="flex gap-2">
            <textarea value={values[expanded]?.text || ''} onChange={e => updateSlot(expanded, { text: e.target.value })} placeholder={`Describe the ${CCC_SLOTS.find(s => s.key === expanded)?.label.toLowerCase()}...`} rows={2} className="flex-1 px-2.5 py-2 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] text-white placeholder-white/20 focus:outline-none resize-none" autoFocus onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); generateFromText(expanded); } }} data-testid={`ad-${expanded}-text`} />
            <button onClick={() => generateFromText(expanded)} disabled={!values[expanded]?.text?.trim() || generating[expanded]} className="self-end p-2.5 rounded-lg disabled:opacity-30" style={{ background: `${CCC_SLOTS.find(s => s.key === expanded)?.color}20` }} data-testid={`ad-${expanded}-gen`}>
              {generating[expanded] ? <Loader2 className="w-4 h-4 animate-spin text-white/40" /> : <Send className="w-4 h-4" style={{ color: CCC_SLOTS.find(s => s.key === expanded)?.color }} />}
            </button>
          </div>
        </div>
      )}

      {/* Edit panel */}
      {editing && (
        <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${CCC_SLOTS.find(s => s.key === editing)?.color}20` }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-white/50">Edit {CCC_SLOTS.find(s => s.key === editing)?.label}</span>
            <button onClick={() => { setEditing(null); setEditFeedback(''); setEditRef(null); }} className="p-0.5"><X className="w-3 h-3 text-white/30" /></button>
          </div>
          <div className="flex gap-2">
            <textarea value={editFeedback} onChange={e => setEditFeedback(e.target.value)} placeholder="Describe changes..." rows={2} className="flex-1 px-2.5 py-2 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] text-white placeholder-white/20 focus:outline-none resize-none" autoFocus onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); iterateImage(editing); } }} data-testid={`ad-${editing}-edit-text`} />
            <div className="flex flex-col gap-1 self-end">
              <input type="file" ref={editRefInput} accept="image/*" className="hidden" onChange={e => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = ev => setEditRef({ file, preview: ev.target.result }); reader.readAsDataURL(file); }} />
              <button onClick={() => editRefInput.current?.click()} className="p-1.5 rounded-lg hover:bg-white/[0.05]" style={{ border: '1px solid rgba(255,255,255,0.08)' }} data-testid={`ad-${editing}-ref`}>
                <ImageIcon className="w-3 h-3 text-white/30" />
              </button>
              <button onClick={() => iterateImage(editing)} disabled={!editFeedback.trim() || generating[editing]} className="p-1.5 rounded-lg disabled:opacity-30" style={{ background: `${CCC_SLOTS.find(s => s.key === editing)?.color}20` }} data-testid={`ad-${editing}-submit`}>
                {generating[editing] ? <Loader2 className="w-3 h-3 animate-spin text-white/40" /> : <Send className="w-3 h-3" style={{ color: CCC_SLOTS.find(s => s.key === editing)?.color }} />}
              </button>
            </div>
          </div>
          {editRef && (
            <div className="flex items-center gap-2 mt-1.5">
              <img src={editRef.preview} alt="ref" className="w-6 h-6 rounded object-cover" />
              <span className="text-[8px] text-white/30">Ref</span>
              <button onClick={() => { setEditRef(null); if (editRefInput.current) editRefInput.current.value = ''; }} className="p-0.5"><X className="w-2 h-2 text-white/30" /></button>
            </div>
          )}
        </div>
      )}

      {/* ── Creative Inputs ── */}
      <div className="space-y-2.5">
        {/* Idea */}
        <div>
          <label className="text-[10px] text-white/50 block mb-1">Ad Idea / Hook</label>
          <textarea
            value={idea}
            onChange={e => setIdea(e.target.value)}
            placeholder="e.g., Funny relatable ad about how dirty people's cars secretly are..."
            rows={2}
            className="w-full px-3 py-2 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] text-white placeholder-white/20 focus:outline-none focus:border-cyan-500/20 resize-none"
            data-testid="idea-input"
          />
        </div>

        {/* Tonality + Aspect Ratio row */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-white/50 block mb-1">Tonality</label>
            <div className="relative">
              <select
                value={tonality}
                onChange={e => setTonality(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-xs bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none focus:border-cyan-500/30 appearance-none cursor-pointer"
                data-testid="tonality-dropdown"
              >
                {TONALITIES.map(t => <option key={t} value={t} className="bg-[#0B0F19]">{t}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-white/50 block mb-1">Aspect Ratio</label>
            <div className="relative">
              <select
                value={aspectRatio}
                onChange={e => setAspectRatio(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-xs bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none focus:border-cyan-500/30 appearance-none cursor-pointer"
                data-testid="aspect-ratio-dropdown"
              >
                <option value="9:16" className="bg-[#0B0F19]">9:16 Reels</option>
                <option value="16:9" className="bg-[#0B0F19]">16:9 YouTube</option>
                <option value="1:1" className="bg-[#0B0F19]">1:1 Feed</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Optional fields row */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Target Audience</label>
            <input value={targetAudience} onChange={e => setTargetAudience(e.target.value)} placeholder="e.g., Gen Z women" className="w-full px-2.5 py-2 rounded-lg text-[11px] bg-white/[0.03] border border-white/[0.05] text-white placeholder-white/15 focus:outline-none" data-testid="audience-input" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">CTA</label>
            <input value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="e.g., Shop Now" className="w-full px-2.5 py-2 rounded-lg text-[11px] bg-white/[0.03] border border-white/[0.05] text-white placeholder-white/15 focus:outline-none" data-testid="cta-input" />
          </div>
        </div>
      </div>

      {/* ── Generate Script Button ── */}
      {product && !script && (
        <button
          onClick={handleGenerateScript}
          disabled={generatingScript}
          className="w-full py-3 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ background: generatingScript ? 'rgba(14,165,233,0.15)' : 'linear-gradient(135deg, #0EA5E9, #0284C7)', boxShadow: generatingScript ? 'none' : '0 4px 16px rgba(14,165,233,0.3)' }}
          data-testid="generate-script-btn"
        >
          {generatingScript ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating Script...</> : <><FileText className="w-4 h-4" /> Generate Script</>}
        </button>
      )}

      {/* ── Script Review ── */}
      {script && (
        <div className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.12)' }} data-testid="script-review">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-emerald-400/80">Ad Script</span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  const text = [script.ad_idea, script.script, script.ending_line].filter(Boolean).join('\n\n');
                  navigator.clipboard.writeText(text);
                  setCopiedScript(true);
                  setTimeout(() => setCopiedScript(false), 1500);
                }}
                className="p-1 rounded hover:bg-white/[0.06] transition-colors"
                title="Copy script"
                data-testid="copy-script-btn"
              >
                {copiedScript ? <Check className="w-2.5 h-2.5 text-emerald-400" /> : <Copy className="w-2.5 h-2.5 text-white/30 hover:text-white/50" />}
              </button>
              <button
                onClick={() => setEditingScript(!editingScript)}
                className={`p-1 rounded transition-colors ${editingScript ? 'bg-emerald-500/15' : 'hover:bg-white/[0.06]'}`}
                title="Edit script"
                data-testid="edit-script-btn"
              >
                <PenLine className={`w-2.5 h-2.5 ${editingScript ? 'text-emerald-400' : 'text-white/30 hover:text-white/50'}`} />
              </button>
              <button onClick={handleGenerateScript} disabled={generatingScript} className="text-[9px] text-white/30 hover:text-white/50 flex items-center gap-1" data-testid="regen-script-btn">
                <RefreshCw className={`w-2.5 h-2.5 ${generatingScript ? 'animate-spin' : ''}`} /> Regenerate
              </button>
            </div>
          </div>
          {script.ad_idea && (
            <div className="px-2.5 py-1.5 rounded-lg bg-emerald-500/5">
              <span className="text-[9px] text-emerald-400/50 block mb-0.5">Idea</span>
              {editingScript ? (
                <textarea
                  value={script.ad_idea}
                  onChange={e => setScript({ ...script, ad_idea: e.target.value })}
                  className="w-full text-[11px] text-white/70 bg-transparent border border-emerald-500/20 rounded px-2 py-1 focus:outline-none focus:border-emerald-500/40 resize-none"
                  rows={2}
                  data-testid="edit-script-idea"
                />
              ) : (
                <p className="text-[11px] text-white/70">{script.ad_idea}</p>
              )}
            </div>
          )}
          <div className="px-3 py-2.5 rounded-lg bg-black/30 font-mono" style={{ border: '1px solid rgba(255,255,255,0.04)' }}>
            {editingScript ? (
              <textarea
                value={script.script}
                onChange={e => setScript({ ...script, script: e.target.value })}
                className="w-full text-[10px] text-white/75 bg-transparent border-none focus:outline-none resize-none leading-relaxed"
                style={{ fontFamily: "'Courier New', monospace", minHeight: '200px' }}
                rows={15}
                data-testid="edit-script-body"
              />
            ) : (
              <pre className="text-[10px] text-white/75 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Courier New', monospace" }}>{script.script}</pre>
            )}
          </div>
          {script.ending_line && (
            editingScript ? (
              <input
                value={script.ending_line}
                onChange={e => setScript({ ...script, ending_line: e.target.value })}
                className="w-full text-[10px] text-white/50 italic px-1 bg-transparent border border-white/10 rounded py-1 focus:outline-none focus:border-emerald-500/30"
                data-testid="edit-script-ending"
              />
            ) : (
              <p className="text-[10px] text-white/50 italic px-1">"{script.ending_line}"</p>
            )
          )}
          {script.cta && <span className="inline-block text-[9px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300/70 border border-cyan-500/10">{script.cta}</span>}

          {/* Generate Shots button */}
          {!shots && (
            <button
              onClick={handleGenerateShots}
              disabled={generatingShots}
              className="w-full py-2.5 rounded-lg text-xs font-semibold text-white transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
              style={{ background: generatingShots ? 'rgba(168,85,247,0.15)' : 'linear-gradient(135deg, #A855F7, #7C3AED)' }}
              data-testid="generate-shots-btn"
            >
              {generatingShots ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating Shots...</> : <><Wand2 className="w-3.5 h-3.5" /> Generate Shot Breakdown</>}
            </button>
          )}
        </div>
      )}

      {/* ── Shot Breakdown Review (Cinematic Markdown) ── */}
      {shots && (
        <div className="space-y-2" data-testid="shot-breakdown">
          <div className="flex items-center justify-between px-1">
            <span className="text-[10px] font-semibold text-violet-400/80">Shot Breakdown</span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shots.markdown || '');
                  setCopiedShots(true);
                  setTimeout(() => setCopiedShots(false), 1500);
                }}
                className="p-1 rounded hover:bg-white/[0.06] transition-colors"
                title="Copy shot breakdown"
                data-testid="copy-shots-btn"
              >
                {copiedShots ? <Check className="w-2.5 h-2.5 text-violet-400" /> : <Copy className="w-2.5 h-2.5 text-white/30 hover:text-white/50" />}
              </button>
              <button
                onClick={() => setEditingShots(!editingShots)}
                className={`p-1 rounded transition-colors ${editingShots ? 'bg-violet-500/15' : 'hover:bg-white/[0.06]'}`}
                title="Edit shot breakdown"
                data-testid="edit-shots-btn"
              >
                <PenLine className={`w-2.5 h-2.5 ${editingShots ? 'text-violet-400' : 'text-white/30 hover:text-white/50'}`} />
              </button>
              <button onClick={handleGenerateShots} disabled={generatingShots} className="text-[9px] text-white/30 hover:text-white/50 flex items-center gap-1" data-testid="regen-shots-btn">
                <RefreshCw className={`w-2.5 h-2.5 ${generatingShots ? 'animate-spin' : ''}`} /> Regen
              </button>
            </div>
          </div>
          <div className="rounded-xl px-3 py-3 max-w-none" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(139,92,246,0.1)' }} data-testid="shot-markdown">
            {editingShots ? (
              <textarea
                value={shots.markdown || ''}
                onChange={e => setShots({ ...shots, markdown: e.target.value })}
                className="w-full text-[10px] text-white/70 bg-transparent border-none focus:outline-none resize-none leading-relaxed font-mono"
                style={{ minHeight: '350px' }}
                rows={25}
                data-testid="edit-shots-body"
              />
            ) : (
              <div className="prose prose-invert prose-xs max-w-none">
                <ReactMarkdown
                  components={{
                    h2: ({children}) => <h2 className="text-sm font-bold text-violet-300/90 mt-3 mb-1.5 border-b border-white/[0.06] pb-1">{children}</h2>,
                    h3: ({children}) => <h3 className="text-[11px] font-bold text-white/80 mt-2.5 mb-1">{children}</h3>,
                    p: ({children}) => <p className="text-[10px] text-white/60 leading-relaxed mb-1">{children}</p>,
                    strong: ({children}) => <strong className="text-white/80 font-semibold">{children}</strong>,
                    li: ({children}) => <li className="text-[10px] text-white/50 ml-2">{children}</li>,
                    hr: () => <hr className="border-white/[0.06] my-2" />,
                  }}
                >
                  {shots.markdown || ''}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Generate Video button */}
          {!videoGenerating && !videoUrl && (
            <button
              onClick={async () => {
                setVideoGenerating(true);
                setVideoUrl(null);
                onVideoStateChange?.(true, null);
                try {
                  // Upload CCC slot files to S3 first (blob URLs can't be fetched by backend)
                  const uploadedSlots = {};
                  for (const key of ['cast', 'costume', 'environment', 'sku']) {
                    uploadedSlots[key] = { ...(values[key] || {}) };
                  }
                  for (const key of ['cast', 'costume', 'environment']) {
                    const slot = values[key] || {};
                    const hasFile = slot.file instanceof File;
                    const hasBlob = slot.preview?.startsWith('blob:');
                    const needsUpload = (hasFile || hasBlob) && !slot.processedUrl?.startsWith('http');
                    if (needsUpload) {
                      try {
                        const fd = new FormData();
                        if (hasFile) {
                          fd.append('image', slot.file);
                        } else if (hasBlob) {
                          // Convert blob URL to file
                          const resp = await fetch(slot.preview);
                          const blob = await resp.blob();
                          fd.append('image', new File([blob], `${key}.png`, { type: 'image/png' }));
                        }
                        fd.append('slot_type', key);
                        const { data: upData } = await axios.post(`${API}/api/recipes/url-to-ad/upload-slot`, fd, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 30000,
                        });
                        if (upData.url) {
                          uploadedSlots[key] = { ...slot, processedUrl: upData.url };
                        }
                      } catch (e) { console.warn(`Upload ${key} failed:`, e); }
                    }
                  }
                  // Also upload any blob product images to S3
                  const uploadedProduct = { ...product };
                  if (product.product_images?.length > 0) {
                    const uploaded = [];
                    for (const imgUrl of product.product_images) {
                      if (imgUrl.startsWith('blob:')) {
                        try {
                          const resp = await fetch(imgUrl);
                          const blob = await resp.blob();
                          const fd = new FormData();
                          fd.append('image', new File([blob], 'sku.png', { type: 'image/png' }));
                          fd.append('slot_type', 'sku');
                          const { data: upData } = await axios.post(`${API}/api/recipes/url-to-ad/upload-slot`, fd, {
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 30000,
                          });
                          uploaded.push(upData.url || imgUrl);
                        } catch { uploaded.push(imgUrl); }
                      } else {
                        uploaded.push(imgUrl);
                      }
                    }
                    uploadedProduct.product_images = uploaded;
                  }
                  const { data } = await axios.post(`${API}/api/recipes/url-to-ad/generate-video`, {
                    product: uploadedProduct, script, shots: shots, tonality, aspectRatio, slots: uploadedSlots,
                    idea, targetAudience, ctaText, sourceUrl: url,
                  }, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
                  if (data.task_id) {
                    const maxPolls = 200;
                    for (let i = 0; i < maxPolls; i++) {
                      await new Promise(r => setTimeout(r, 4000));
                      let pdata;
                      try {
                        const resp = await axios.get(`${API}/api/recipes/url-to-ad/task/${data.task_id}`, {
                          headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
                        });
                        pdata = resp.data;
                      } catch { continue; }
                      if (pdata.status === 'done' && pdata.video_url) {
                        setVideoUrl(pdata.video_url);
                        onVideoStateChange?.(false, pdata.video_url);
                        break;
                      }
                      if (pdata.status === 'failed') {
                        alert(pdata.error || 'Video generation failed');
                        onVideoStateChange?.(false, null);
                        break;
                      }
                    }
                  }
                } catch (err) {
                  alert(err.message || 'Failed to start video generation');
                  onVideoStateChange?.(false, null);
                }
                finally { setVideoGenerating(false); }
              }}
              className="w-full py-3 rounded-xl text-xs font-bold text-white transition-all flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}
              data-testid="generate-video-btn"
            >
              <Sparkles className="w-4 h-4" /> Generate Video
            </button>
          )}
          {videoGenerating && (
            <div className="flex items-center gap-2 py-2 px-3 rounded-lg" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.12)' }}>
              <Loader2 className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
              <span className="text-[10px] text-emerald-400/70">Generating video — see left panel</span>
            </div>
          )}
          {videoUrl && !videoGenerating && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] text-emerald-400/80 flex-1">Video ready — see left panel</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setUrl(''); setProduct(null); setScript(null); setShots(null);
                    setIdea(''); setTargetAudience(''); setCtaText('');
                    setTonality('Informative'); setAspectRatio('9:16');
                    setVideoUrl(null); setVideoGenerating(false);
                    onChange({ cast: {}, costume: {}, environment: {}, sku: {} });
                    onReset?.();
                  }}
                  className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-cyan-400/70 transition-all flex items-center justify-center gap-2 hover:text-cyan-300"
                  style={{ background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.12)' }}
                  data-testid="new-ad-right-btn"
                >
                  <X className="w-3.5 h-3.5" /> New Ad
                </button>
                <button
                  onClick={() => { setVideoUrl(null); onVideoStateChange?.(false, null); }}
                  className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white/70 transition-all flex items-center justify-center gap-2 hover:text-white"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="generate-again-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Generate Again
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="ad-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full z-10">
            <X className="w-5 h-5 text-white" />
          </button>
          <span className="absolute top-5 left-4 text-xs text-white/50">{lightbox.label}</span>
          <img src={lightbox.src} alt={lightbox.label} className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
